import { onReducedMotionChange, prefersReducedMotion } from "../helpers/reducedMotion.js";

// The animated equaliser behind /mcp/ (#237), ported from the "Artist Growth Home" design's
// canvas. Columns of square cells rise from the bottom of the screen like an EQ meter,
// pulsing on a beat, with a peak cell that flares when a column jumps. They swell toward
// the pointer. Unlike the design, which settles them low once the reader scrolls past the
// hero, they hold at least half the screen all the way down, footer included.
//
// Pure decoration: the canvas is aria-hidden and pointer-events: none, and the page reads
// the same without it. Under reduced motion it draws one still frame instead of a loop.

// The design's palette, brightest first: the field's own ramp of greens, darker than
// $teal so it sits behind the page instead of competing with its accents.
const PALETTE = ["#2bb3a3", "#22988b", "#1b7a6e", "#135443", "#0e3a30", "#0a2620"];
const FLARE = "#9af0e4";
const FLARE_GLOW = "#5fd3c4";
// How far, in cells, a column has to jump past its held peak in one redraw to flare. At
// 30fps that comes to roughly one flare per column every five or six seconds.
const FLARE_JUMP = 0.4;
// The design's defaults for the props its canvas exposed.
const BPM = 104;
const BANDS = 12;
// A third of the design's 0.6, so the field sits well back on /mcp/. The peak flares
// draw at full strength in the design, so they dim by the same ratio.
const DESIGN_OPACITY = 0.6;
const OPACITY = 0.2;
// Under reduced motion the still frame is the field at this many seconds in, as in the
// design: t = 0 is all troughs.
const STILL_T = 4;

// Redraw cap and slow-device fallback, identical to modules/ridge-field.js, which explains
// both at length: uncapped, a canvas loop kept CI's viewport suite from ever reaching
// network idle (#242), and a device that could not redraw MIN_FPS times a second uncapped
// gets a still frame instead.
const FPS = 30;
const FRAME_MS = 1000 / FPS - 2;
const MIN_FPS = 40;
const WINDOW_MS = 1500;
const MAX_GAP_MS = 50;

// A fixed pseudo-random value per cell, so the sparse cells above each column flicker in
// the same places frame to frame instead of shimmering.
const hash = (a, b) => {
    const s = Math.sin(a * 127.1 + b * 311.7) * 43758.5453;

    return s - Math.floor(s);
};

class EqField {
    constructor(canvas) {
        this.canvas = canvas;
        this.ctx = canvas.getContext("2d");
        this.raf = null;
        this.resizeRaf = null;
        this.t0 = performance.now();
        this.width = 0;
        this.height = 0;
        this.dpr = 0;
        this.lastDraw = -Infinity;
        this.t = 0;
        this.drew = false;
        this.windowStart = 0;
        this.windowDraws = 0;
        this.windowCost = 0;
        this.slowWindows = 0;
        this.tooSlow = false;
        // The meter: each band eases toward a level picked fresh on every beat.
        this.eq = new Float64Array(BANDS);
        this.target = new Float64Array(BANDS);
        this.lastBeat = -1;
        // Per column: the falling peak, and how lit its flare still is.
        this.peaks = [];
        this.flares = [];
        this.pointerX = null;

        if (!this.ctx) return;

        this.resize();
        window.addEventListener("resize", this.handleResize);
        window.addEventListener("pointermove", (e) => (this.pointerX = e.clientX), { passive: true });
        document.addEventListener("pointerleave", () => (this.pointerX = null));
        // See the matching handler in ridge-field.js.
        document.addEventListener("visibilitychange", () => {
            this.windowStart = 0;
            this.drew = false;
        });
        onReducedMotionChange(this.handleMotionPreference);
        this.handleMotionPreference();
    }

    // Returns false when nothing changed; see ridge-field.js.
    resize() {
        const dpr = Math.min(window.devicePixelRatio || 1, 2);
        const width = this.canvas.clientWidth;
        const height = this.canvas.clientHeight;

        if (width === this.width && height === this.height && dpr === this.dpr) return false;

        this.width = width;
        this.height = height;
        this.dpr = dpr;
        this.canvas.width = width * dpr;
        this.canvas.height = height * dpr;
        this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        this.cell = width < 700 ? 20 : 28;
        this.cols = Math.ceil(width / this.cell) + 1;
        this.rows = Math.ceil(height / this.cell) + 1;
        // A new column count starts every peak afresh rather than inheriting a neighbour's.
        this.peaks = [];
        this.flares = [];

        return true;
    }

    handleResize = () => {
        if (this.resizeRaf) return;

        this.resizeRaf = requestAnimationFrame((now) => {
            this.resizeRaf = null;

            if (!this.resize()) return;

            if (this.raf) {
                this.lastDraw = now;
                this.drew = false;
                this.redraw((now - this.t0) / 1000, 0);
            } else {
                this.redraw(this.t, 0);
            }
        });
    };

    handleMotionPreference = () => {
        cancelAnimationFrame(this.raf);
        this.raf = null;

        if (prefersReducedMotion() || this.tooSlow) {
            this.redraw(STILL_T, 0);
        } else {
            this.windowStart = 0;
            this.drew = false;
            this.raf = requestAnimationFrame(this.frame);
        }
    };

    frame = (now) => {
        if (this.drew) {
            this.drew = false;
            this.windowDraws++;
            this.windowCost += Math.min(now - this.lastDraw, MAX_GAP_MS);
        }

        if (now - this.lastDraw < FRAME_MS) {
            this.raf = requestAnimationFrame(this.frame);
            return;
        }

        // Seconds since the last redraw, for the falling peaks. Capped, so the first
        // redraw and one after a hidden tab do not drop every peak to the floor at once.
        const dt = Math.min(0.05, (now - this.lastDraw) / 1000);

        this.lastDraw = now;
        this.drew = true;
        this.redraw((now - this.t0) / 1000, dt);

        if (this.keepingUp(now)) {
            this.raf = requestAnimationFrame(this.frame);
        } else {
            this.tooSlow = true;
            this.raf = null;
        }
    };

    // False once two windows in a row come in under MIN_FPS; see ridge-field.js.
    keepingUp(now) {
        if (!this.windowStart) {
            this.windowStart = now;
            this.windowDraws = 0;
            this.windowCost = 0;
            return true;
        }

        if (now - this.windowStart < WINDOW_MS) return true;

        const slow = this.windowCost > 0 && (this.windowDraws * 1000) / this.windowCost < MIN_FPS;

        this.slowWindows = slow ? this.slowWindows + 1 : 0;
        this.windowStart = now;
        this.windowDraws = 0;
        this.windowCost = 0;

        return this.slowWindows < 2;
    }

    redraw(t, dt) {
        this.t = t;
        this.draw(t, dt);
    }

    draw(t, dt) {
        const { ctx, width: W, height: H, cell, cols, rows, eq, target, peaks, flares } = this;
        const beatLen = 60 / BPM;
        const beat = Math.floor(t / beatLen);
        const phase = (t % beatLen) / beatLen;

        // A new level for every band on each beat, louder on the downbeat.
        if (beat !== this.lastBeat) {
            this.lastBeat = beat;

            for (let i = 0; i < BANDS; i++) target[i] = Math.pow(Math.random(), 1.6) * (beat % 4 === 0 ? 1 : 0.7);
        }

        for (let i = 0; i < BANDS; i++) eq[i] += (target[i] - eq[i]) * 0.05;

        // The kick: a jolt at the start of every beat, fuller on alternate ones.
        const kick = Math.exp(-phase * 5) * (beat % 2 === 0 ? 1 : 0.55);
        const size = cell - 6;
        // The cells, batched by shade: one path per palette entry, filled once each after
        // the loop, rather than a fillStyle change and a fill for every one of the ~1,400
        // cells a frame. The flares still go down first, so the cells cover them as before.
        const shades = PALETTE.map(() => new Path2D());

        ctx.clearRect(0, 0, W, H);

        for (let c = 0; c < cols; c++) {
            // The column's EQ level, interpolated between its two nearest bands.
            const bf = (c / cols) * (BANDS - 1);
            const b0 = Math.floor(bf);
            const b1 = Math.min(BANDS - 1, b0 + 1);
            const level = eq[b0] + (eq[b1] - eq[b0]) * (bf - b0);
            const swell = Math.sin(c * 0.11 - t * 1.1);
            // The column height, in cells: three rolling waves, between 0.46 and 0.7 of the
            // screen together, plus the EQ and the kick on top.
            const wave = 0.6 * swell + 0.3 * Math.sin(c * 0.045 + t * 0.5) + 0.1 * Math.sin(c * 0.23 - t * 1.7);
            let L = rows * (0.58 + 0.12 * wave) + level * rows * 0.08 + kick * 0.5 * (0.5 + 0.5 * swell);

            if (this.pointerX !== null) {
                const dx = (c * cell - this.pointerX) / (cell * 5);

                L += Math.exp(-dx * dx) * rows * 0.07;
            }

            // The peak holds the column's highest point and falls back slowly, never below
            // the column itself; a jump well past it lights the flare. Without that floor
            // the peak sinks under a still column and re-flares about three times a second.
            if (peaks[c] === undefined || L > peaks[c] + FLARE_JUMP) {
                if (peaks[c] !== undefined) flares[c] = 1;
                peaks[c] = L;
            } else {
                peaks[c] = Math.max(peaks[c] - dt * 2.2, L);
                flares[c] = Math.max(0, (flares[c] || 0) - dt);
            }

            const drift = Math.sin(c * 0.24 + t * 0.6) * cell * 0.05;

            if (flares[c] > 0) {
                const y = (rows - 2 - Math.floor(peaks[c])) * cell + drift;

                ctx.save();
                ctx.globalAlpha = Math.min(1, flares[c] * 1.4) * (OPACITY / DESIGN_OPACITY);
                ctx.fillStyle = FLARE;
                ctx.shadowColor = FLARE_GLOW;
                ctx.shadowBlur = 14 * flares[c];
                ctx.fillRect(c * cell + 3, y + 3, size, size);
                ctx.restore();
            }

            // Solid up to the level, then five rows of sparse, shrinking cells above it.
            const top = Math.min(rows, Math.ceil(L) + 5);

            for (let rb = 0; rb < top; rb++) {
                const d = L - rb;
                const h = hash(c, rb);
                let scale = 1;
                let shade;

                if (d > 0) {
                    shade = Math.floor((1 - Math.min(1, rb / Math.max(1, L))) * 3.2 + hash(rb, c) * 2.6);
                } else {
                    const f = -d / 5;

                    if (h < 0.35 + f * 0.9) continue;

                    scale = 0.2 + (1 - f) * 0.55 * (0.5 + h * 0.5);
                    shade = Math.floor(hash(rb, c) * 2.2);
                }

                const x = c * cell + Math.sin(rb * 0.28 + t * 0.7) * cell * 0.03;
                const y = (rows - 1 - rb) * cell + drift - kick * 0.8 * (d > 0 ? 1 : 0.4);
                const s = size * scale;

                // Square to the grid: the design tilts each cell by at most 0.01 rad, about
                // 0.1px at the corners, which no one can see and which cost a transform per cell.
                shades[Math.max(0, Math.min(PALETTE.length - 1, shade))].rect(x + (cell - s) / 2, y + (cell - s) / 2, s, s);
            }
        }

        ctx.globalAlpha = OPACITY;
        shades.forEach((path, i) => {
            ctx.fillStyle = PALETTE[i];
            ctx.fill(path);
        });
    }
}

document.querySelectorAll("[data-eq-field]").forEach((canvas) => new EqField(canvas));
